import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HTTPCONSTANTS } from '../http/http-constants';
import {
  AuctionFeeLine,
  AuctionHouseSummary,
  AuctionLotByRegistration,
  AuctionLotChange,
  AuctionLotDetailDto,
  AuctionLotDto,
  AuctionLotsQuery,
  AuctionLotsResponse,
  AuctionTab,
} from '../models/auction-lot';
import { showUnfinished } from '../site-config';

/** Same gate as app.module `isDevOnly`: dev2 host or a non-production build. */
export const AUCTIONS_DEV_ONLY = showUnfinished;

interface MockFixture {
  capturedAt: string;
  houses: AuctionHouseSummary[];
  lots: AuctionLotDto[];
  details: { [id: string]: { bidHistory: any[]; attempts: any[] } };
}

/**
 * Live Auctions data (PlatexAPI `/api/Auction/lots*`).
 *
 * DEV-ONLY mock mode: when the page URL carries `?mock=1` on dev2/local, every
 * call is answered from `assets/dev-fixtures/live-auctions.json` (real lots
 * captured 16 Oct 2026 for the approved prototype), time-shifted so the capture
 * moment equals page load. Never active on production builds/hosts.
 */
@Injectable({ providedIn: 'root' })
export class AuctionLotsService {
  private base = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/Auction`;

  /** Live-lot count for the nav-shell pill (null = unknown / hide). */
  public readonly liveCount$ = new BehaviorSubject<number | null>(null);

  public readonly mock: boolean =
    AUCTIONS_DEV_ONLY &&
    typeof window !== 'undefined' &&
    /[?&]mock=1(&|$)/.test(window.location.search);

  private rawHttp: HttpClient;
  private fixture$?: Observable<MockFixture>;
  private liveCountRequested = false;

  constructor(private http: HttpClient, httpBackend: HttpBackend) {
    // Fixture fetch bypasses the interceptors (the default UrlInterceptor
    // branch would append plato-search query suffixes to an asset URL).
    this.rawHttp = new HttpClient(httpBackend);
  }

  public getLots(query: AuctionLotsQuery): Observable<AuctionLotsResponse> {
    const req$ = this.mock
      ? this.mockLots(query)
      : this.http.get<AuctionLotsResponse>(`${this.base}/lots`, { params: this.toParams(query) });
    // Only an unfiltered query's live count is the site-wide number for the nav
    // pill — a plate page's ?q=REG lookup would otherwise set it to 1.
    const unfiltered = !query.q && (!query.house || query.house === 'all') && (!query.style || query.style === 'all')
      && (!query.reserve || query.reserve === 'all') && query.minBid == null && query.maxBid == null
      && !query.sale && !query.rt;
    return req$.pipe(tap((r) => unfiltered && r && r.counts && this.liveCount$.next(r.counts.live)));
  }

  public getLot(id: string): Observable<AuctionLotDetailDto> {
    if (this.mock) return this.mockLot(id);
    return this.http.get<AuctionLotDetailDto>(`${this.base}/lots/${encodeURIComponent(id)}`);
  }

  public getChanges(since: string): Observable<AuctionLotChange[]> {
    // Mock: no bid simulation (dropped from the prototype on purpose).
    if (this.mock) return of([]);
    return this.http.get<AuctionLotChange[]>(`${this.base}/lots/changes`, {
      params: new HttpParams().set('since', since),
    });
  }

  /**
   * Live / coming-soon lots for a set of registrations (compact keys), as a
   * Map keyed by registrationKey. One POST per 5,000 keys. Fails silent: any
   * error (incl. 404 before the endpoint is deployed) yields an empty map.
   */
  public getLotsByRegistrations(regs: string[]): Observable<Map<string, AuctionLotDto>> {
    const keys = Array.from(
      new Set((regs || []).map((r) => (r || '').replace(/\s+/g, '').toUpperCase()).filter(Boolean))
    );
    if (!keys.length || !AUCTIONS_DEV_ONLY) return of(new Map());
    if (this.mock) return this.mockByRegistrations(keys);
    const chunks: string[][] = [];
    for (let i = 0; i < keys.length; i += 5000) chunks.push(keys.slice(i, i + 5000));
    return forkJoin(
      chunks.map((c) =>
        this.http
          .post<{ items: AuctionLotByRegistration[] }>(`${this.base}/lots/by-registrations`, { regs: c })
          .pipe(catchError(() => of({ items: [] as AuctionLotByRegistration[] })))
      )
    ).pipe(
      map((rs) => {
        const out = new Map<string, AuctionLotDto>();
        rs.forEach((r) =>
          (r?.items || []).forEach((it) => {
            if (it && it.lot) out.set((it.registrationKey || it.lot.registrationKey || '').toUpperCase(), it.lot);
          })
        );
        return out;
      }),
      catchError(() => of(new Map<string, AuctionLotDto>()))
    );
  }

  /** One cheap request for the nav pill; later page loads keep it fresh. */
  public ensureLiveCount(): void {
    if (this.liveCountRequested || !AUCTIONS_DEV_ONLY) return;
    this.liveCountRequested = true;
    this.getLots({ tab: 'live', page: 1, pageSize: 1 })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  private toParams(query: AuctionLotsQuery): HttpParams {
    let p = new HttpParams();
    Object.keys(query).forEach((k) => {
      const v = (query as any)[k];
      if (v !== null && v !== undefined && v !== '') p = p.set(k, String(v));
    });
    return p;
  }

  // ------------------------------------------------------------------ mock
  private loadFixture(): Observable<MockFixture> {
    if (!this.fixture$) {
      this.fixture$ = this.rawHttp.get<MockFixture>('assets/dev-fixtures/live-auctions.json').pipe(
        map((f) => this.timeShift(f)),
        shareReplay(1)
      );
    }
    return this.fixture$;
  }

  private timeShift(f: MockFixture): MockFixture {
    const delta = Date.now() - Date.parse(f.capturedAt);
    const shift = (s: string | null) => (s ? new Date(Date.parse(s) + delta).toISOString() : s);
    f.lots.forEach((l) => {
      l.biddingOpensAt = shift(l.biddingOpensAt);
      l.biddingClosesAt = shift(l.biddingClosesAt);
      l.observedAt = shift(l.observedAt);
    });
    f.houses.forEach((h) => (h.lastCheckedAt = shift(h.lastCheckedAt)));
    Object.keys(f.details || {}).forEach((id) =>
      (f.details[id].bidHistory || []).forEach((b) => {
        b.at = shift(b.at);
        b.approxAt = shift(b.approxAt);
      })
    );
    return f;
  }

  /** Fixture lot with status/result recomputed against the real clock. */
  private mockPhase(l: AuctionLotDto, now: number): AuctionLotDto {
    const opens = l.biddingOpensAt ? Date.parse(l.biddingOpensAt) : 0;
    const closes = l.biddingClosesAt ? Date.parse(l.biddingClosesAt) : Infinity;
    if (l.status === 'soon' && now < opens) return { ...l, status: 'soon' };
    if (now >= closes) {
      const bid = l.currentBidPence || 0;
      // Mirrors the backend: CC outcome inferred from reserve-met at close;
      // RT confirmed next morning. Hammer = last bid PlateX saw (inferred).
      const result =
        l.house === 'collecting_cars' ? (l.reserveStatus === 'met' && bid > 0 ? 'sold' : 'not_sold') : 'pending';
      return {
        ...l,
        status: 'ended',
        resultStatus: result,
        resultInferred: result !== 'pending',
        hammerPence: bid || null,
        hammerInferred: !!bid,
      };
    }
    return { ...l, status: 'live' };
  }

  private mockLots(q: AuctionLotsQuery): Observable<AuctionLotsResponse> {
    return this.loadFixture().pipe(
      map((f) => {
        const now = Date.now();
        const lots = f.lots.map((l) => this.mockPhase(l, now));
        const londonDay = (t: number) => new Date(t).toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
        const today = londonDay(now);
        const inTab = (l: AuctionLotDto, tab: AuctionTab) => {
          if (tab === 'live') return l.status === 'live';
          if (tab === 'today') return l.status === 'live' && londonDay(Date.parse(l.biddingClosesAt!)) === today;
          if (tab === 'soon') return l.status === 'soon';
          return l.status === 'ended';
        };
        const tab = (q.tab || 'live') as AuctionTab;
        const term = (q.q || '').replace(/\s/g, '').toUpperCase();
        const min = q.minBid != null ? q.minBid * 100 : null;
        const max = q.maxBid != null ? q.maxBid * 100 : null;
        let items = lots.filter(
          (l) =>
            inTab(l, tab) &&
            (!term || l.registrationKey.includes(term)) &&
            (!q.house || q.house === 'all' || l.house === q.house) &&
            (!q.style || q.style === 'all' || l.style === q.style) &&
            (!q.reserve || q.reserve === 'all' || l.reserveStatus === q.reserve) &&
            (min == null || (l.currentBidPence || 0) >= min) &&
            (max == null || (l.currentBidPence || 0) < max)
        );
        const t = (s: string | null) => (s ? Date.parse(s) : 0);
        const bid = (l: AuctionLotDto) => l.currentBidPence || 0;
        const gap = (l: AuctionLotDto) =>
          l.lastSold && bid(l) > 0 ? (l.lastSold.pricePence - bid(l)) / l.lastSold.pricePence : -9;
        const sorters: { [k: string]: (a: AuctionLotDto, b: AuctionLotDto) => number } = {
          ending: (a, b) =>
            tab === 'soon'
              ? t(a.biddingOpensAt) - t(b.biddingOpensAt)
              : tab === 'results'
              ? t(b.biddingClosesAt) - t(a.biddingClosesAt)
              : t(a.biddingClosesAt) - t(b.biddingClosesAt),
          high: (a, b) => bid(b) - bid(a),
          low: (a, b) => bid(a) - bid(b),
          bids: (a, b) => (b.bidCount ?? -1) - (a.bidCount ?? -1),
          below: (a, b) => gap(b) - gap(a),
          registration: (a, b) => a.registrationKey.localeCompare(b.registrationKey),
          house: (a, b) => a.houseName.localeCompare(b.houseName),
          status: (a, b) => a.status.localeCompare(b.status),
          price: (a, b) => bid(b) - bid(a),
          allin: (a, b) => (b.allInPence || 0) - (a.allInPence || 0),
          reserve: (a, b) => a.reserveStatus.localeCompare(b.reserveStatus),
          lastsold: (a, b) => (b.lastSold?.pricePence || 0) - (a.lastSold?.pricePence || 0),
        };
        // Mock only: each sorter encodes the column's natural direction; an explicit
        // dir opposite to it reverses the order.
        const natural: { [k: string]: string } = { registration: 'asc', house: 'asc', status: 'asc', reserve: 'asc', ending: 'asc' };
        items = items.sort(sorters[q.sort || 'ending'] || sorters['ending']);
        if (q.dir && q.dir !== (natural[q.sort || 'ending'] || 'desc')) items.reverse();
        const total = items.length;
        const pageSize = q.pageSize || 48;
        const page = Math.max(1, q.page || 1);
        const live = lots.filter((l) => l.status === 'live');
        return {
          items: items.slice((page - 1) * pageSize, page * pageSize),
          total,
          counts: {
            live: live.length,
            today: lots.filter((l) => inTab(l, 'today')).length,
            soon: lots.filter((l) => l.status === 'soon').length,
            results: lots.filter((l) => l.status === 'ended').length,
          },
          houses: f.houses.map((h) => ({ ...h, live: live.filter((l) => l.house === h.key).length })),
          liveBidTotalPence: live.reduce((s, l) => s + (l.currentBidPence || 0), 0),
          generatedAt: new Date(now).toISOString(),
        } as AuctionLotsResponse;
      })
    );
  }

  /**
   * Mock: fixture lots that match (live/soon only), plus demos so any search
   * can be reviewed locally: FRE 5H (or else the first requested reg) live at
   * Brightwells, current bid £6,500, closing in ~2.6 days; the next requested
   * reg coming to Reg Transfers tomorrow 19:30. DEV-only (?mock=1).
   */
  private mockByRegistrations(keys: string[]): Observable<Map<string, AuctionLotDto>> {
    return this.loadFixture().pipe(
      map((f) => {
        const want = new Set(keys);
        const now = Date.now();
        const out = new Map<string, AuctionLotDto>();
        f.lots
          .filter((l) => want.has(l.registrationKey))
          .map((l) => this.mockPhase(l, now))
          .filter((l) => l.status !== 'ended')
          .forEach((l) => out.set(l.registrationKey, l));
        // Live demo: FRE 5H when requested, else the first requested key that
        // has no fixture lot (so any search shows the treatment in review).
        const liveKey = want.has('FRE5H') ? 'FRE5H' : keys.find((k) => !out.has(k));
        if (liveKey) {
          out.set(liveKey, {
            id: 'bw-demo-' + liveKey.toLowerCase(),
            house: 'brightwells',
            houseName: 'Brightwells',
            registration: liveKey === 'FRE5H' ? 'FRE 5H' : liveKey,
            registrationKey: liveKey,
            lotUrl: 'https://www.brightwells.com/',
            status: 'live',
            resultStatus: null,
            currentBidPence: 650000,
            hammerPence: null,
            bidCount: null,
            reserveStatus: 'unknown',
            reserveLowered: false,
            noVat: false,
            buyNowPence: null,
            buyNowAvailable: false,
            biddingOpensAt: null,
            biddingClosesAt: new Date(now + 2.6 * 864e5).toISOString(),
            observedAt: new Date(now - 5 * 6e4).toISOString(),
            style: 'dateless',
            allInPence: null,
            lastSold: null,
            previousAttempts: null,
          });
        }
        // Second demo: the first other requested key becomes a Reg Transfers
        // coming-soon lot (opens tomorrow 19:30 London), so both states show.
        const soonKey = keys.find((k) => !out.has(k));
        if (liveKey && soonKey) {
          const opens = new Date(now + 864e5);
          opens.setUTCHours(18, 30, 0, 0);
          out.set(soonKey, {
            ...out.get(liveKey)!,
            id: 'rt-demo-' + soonKey,
            house: 'regtransfers',
            houseName: 'Reg Transfers',
            registration: soonKey,
            registrationKey: soonKey,
            lotUrl: 'https://www.regtransfers.co.uk/auction/bid/' + soonKey.toLowerCase(),
            status: 'soon',
            currentBidPence: null,
            startingPricePence: null,
            biddingOpensAt: opens.toISOString(),
            biddingClosesAt: new Date(opens.getTime() + 7 * 864e5).toISOString(),
          });
        }
        return out;
      }),
      catchError(() => of(new Map<string, AuctionLotDto>()))
    );
  }

  private mockLot(id: string): Observable<AuctionLotDetailDto> {
    return this.loadFixture().pipe(
      map((f) => {
        const raw = f.lots.find((l) => l.id === id);
        if (!raw) throw new Error('lot not found');
        const l = this.mockPhase(raw, Date.now());
        const d = (f.details || {})[id] || { bidHistory: [], attempts: [] };
        return { ...l, feeBreakdown: mockFees(l), bidHistory: d.bidHistory, attempts: d.attempts };
      })
    );
  }
}

/** Mirrors the prototype's per-house buyer-fee rules (mock only). */
function mockFees(l: AuctionLotDto): AuctionFeeLine[] {
  if (l.currentBidPence == null) return [];
  const b = l.currentBidPence;
  if (l.house === 'regtransfers') {
    return [
      { label: 'Current bid', pence: b, note: null },
      { label: 'VAT on bid', pence: l.noVat ? 0 : Math.round(b * 0.2), note: l.noVat ? 'No VAT lot' : null },
      { label: 'DVLA transfer fee', pence: 8000, note: null },
    ];
  }
  if (l.house === 'collecting_cars') {
    const prem = Math.max(Math.round(b * 0.12), 30000);
    return [
      { label: 'Current bid', pence: b, note: null },
      { label: "Buyer's premium inc. VAT", pence: prem, note: b * 0.12 < 30000 ? '£300 minimum' : '10% + VAT' },
    ];
  }
  return [{ label: 'Current bid', pence: b, note: null }];
}
